import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, Ban, Crown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useServiceMap } from "@/lib/data";
import { Avatar } from "@/components/Bits";

const ST = {
  pending: ["Em análise", "bg-yellow-100 text-yellow-800"],
  approved: ["Aprovado", "bg-green-100 text-green-800"],
  rejected: ["Reprovado", "bg-red-100 text-red-700"],
  blocked: ["Bloqueado", "bg-red-600 text-white"],
};
const FILTERS = [
  ["", "Todos", "providers_total"], ["pending", "Em análise", "providers_pending"], ["approved", "Aprovados", "providers_approved"],
  ["rejected", "Reprovados", "providers_rejected"], ["blocked", "Bloqueados", "providers_blocked"], ["disponiveis", "Disponíveis agora", "providers_available"],
];
const stLabel = (s) => ST[s] || [s || "Sem status", "bg-slate-200 text-slate-700"];

function ProRow({ p, reload }) {
  const [until, setUntil] = useState(p.pro_until ? p.pro_until.slice(0, 10) : "");
  const save = async (value) => {
    try { await api.post(`/admin/providers/${p.user_id}/pro`, { until: value || null }); toast.success(value ? "Plano Pro liberado" : "Plano Pro removido"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Crown className="h-5 w-5 text-orange-500" />
      <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="po-input min-h-[48px] w-auto" data-testid={`admin-pro-date-${p.user_id}`} />
      <button onClick={() => save(until)} disabled={!until} className="po-btn-dark min-h-[48px] text-base" data-testid={`admin-pro-save-${p.user_id}`}>Liberar Pro até</button>
      {p.is_pro && <button onClick={() => save(null)} className="po-btn-outline min-h-[48px] text-base" data-testid={`admin-pro-remove-${p.user_id}`}>Remover Pro</button>}
    </div>
  );
}

function ProviderItem({ p, reload }) {
  const services = useServiceMap();
  const setStatus = async (status) => {
    try { await api.post(`/admin/providers/${p.user_id}/status`, { status }); toast.success("Status atualizado"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const btn = "po-btn min-h-[48px] px-4 text-base";
  return (
    <div className="po-card" data-testid={`admin-provider-${p.user_id}`}>
      <div className="flex flex-wrap items-start gap-4">
        <Avatar photo={p.photo} name={p.name} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/prestador/${p.user_id}`} className="text-xl font-bold text-slate-900 hover:text-orange-600">{p.name}</Link>
            <span className={`rounded-full px-3 py-0.5 text-sm font-bold ${stLabel(p.status)[1]}`} data-testid={`admin-provider-status-${p.user_id}`}>{stLabel(p.status)[0]}</span>
            {p.is_test && <span className="rounded-full bg-slate-200 px-3 py-0.5 text-sm font-bold text-slate-700">teste</span>}
            {p.available && <span className="rounded-full bg-green-600 px-3 py-0.5 text-sm font-bold text-white">disponível</span>}
            {p.is_pro && <span className="rounded-full bg-orange-500 px-3 py-0.5 text-sm font-bold text-white">Pro</span>}
          </div>
          <p className="text-base text-slate-600">{p.email} · WhatsApp {p.whatsapp}</p>
          <p className="text-base text-slate-700">{p.services.map((s) => services[s]?.name || s).join(", ")} · {p.base_neighborhood}, {p.base_city}</p>
          <p className="text-base font-semibold text-slate-800" data-testid={`admin-provider-counter-${p.user_id}`}>
            Pedidos aceitos: {p.accepted_count || 0} · Grátis restantes: {p.is_pro ? "Pro" : p.free_left}
          </p>
        </div>
      </div>
      {p.status === "pending" ? (
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid={`admin-pending-actions-${p.user_id}`}>
          <button onClick={() => setStatus("approved")} className={`${btn} min-h-[60px] bg-green-600 text-lg text-white hover:bg-green-700`} data-testid={`admin-approve-${p.user_id}`}><Check className="h-6 w-6" /> Aprovar</button>
          <button onClick={() => setStatus("rejected")} className={`${btn} min-h-[60px] border-2 border-red-400 bg-white text-lg text-red-700 hover:bg-red-50`} data-testid={`admin-reject-${p.user_id}`}><X className="h-6 w-6" /> Reprovar</button>
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {!["approved", "pending"].includes(p.status) && <button onClick={() => setStatus("approved")} className={`${btn} bg-green-600 text-white hover:bg-green-700`} data-testid={`admin-approve-${p.user_id}`}><Check className="h-5 w-5" /> Aprovar</button>}
        {p.status !== "blocked" && <button onClick={() => setStatus("blocked")} className={`${btn} bg-red-600 text-white hover:bg-red-700`} data-testid={`admin-block-${p.user_id}`}><Ban className="h-5 w-5" /> Bloquear</button>}
        {p.status === "blocked" && <button onClick={() => setStatus("pending")} className={`${btn} border-2 border-slate-300`} data-testid={`admin-unblock-${p.user_id}`}><RotateCcw className="h-5 w-5" /> Desbloquear (voltar à análise)</button>}
      </div>
      <ProRow p={p} reload={reload} />
    </div>
  );
}

export default function AdminProviders({ filter = "", setFilter, counts }) {
  const qc = useQueryClient();
  const { data = [], isFetching } = useQuery({
    queryKey: ["admin-providers", filter],
    queryFn: () => api.get("/admin/providers", { params: filter ? { status: filter } : {} }).then((r) => r.data),
  });
  const reload = () => { qc.invalidateQueries({ queryKey: ["admin-providers"] }); qc.invalidateQueries({ queryKey: ["admin-overview"] }); };
  const current = FILTERS.find(([v]) => v === filter) || FILTERS[0];
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([v, l, k]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`admin-provider-filter-${v || "all"}`} aria-pressed={filter === v}
            className={`min-h-[44px] rounded-full border-2 px-4 text-base font-bold ${filter === v ? "border-orange-500 bg-orange-500 text-white" : "border-slate-300 text-slate-700"}`}>
            {l} ({counts?.[k] ?? "–"})
          </button>
        ))}
      </div>
      <p className="mt-4 text-lg font-semibold text-slate-700" data-testid="admin-providers-showing">
        Mostrando: {current[1]} · {isFetching ? "..." : data.length} prestador(es)
      </p>
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        {data.map((p) => <ProviderItem key={p.user_id} p={p} reload={reload} />)}
        {!isFetching && data.length === 0 && <p className="text-lg text-slate-600" data-testid="admin-providers-empty">Nenhum prestador aqui</p>}
      </div>
    </div>
  );
}
